import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FieldHeader, FieldHelpText } from './FieldParts';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

const toNum = (v: unknown, fallback: number): number => {
  if (v === '' || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Slider (Elements Library): a value in a bounded range with a live readout.
 * A custom accent track/fill/thumb renders the position; a transparent native
 * range input sits on top so keyboard + drag interaction and a11y come for
 * free. Value is stored as the raw number (string).
 */
const SliderInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    unit?: string;
    showValue?: boolean;
    defaultValue?: number | string;
  };

  const label = data?.label || 'Slider';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = !!data?.required;
  const min = toNum(data?.min, 0);
  const max = Math.max(toNum(data?.max, 100), min + 1);
  const step = Math.max(toNum(data?.step, 1), 0);
  const unit = data?.unit || '';
  const showValue = data?.showValue !== false;

  // A slider always shows a position: hydrate to the configured default (or
  // the minimum) so the thumb + stored value are in sync from the start.
  const defaultForHydration = String(toNum(data?.defaultValue, min));
  const displayValue = useDefaultValue(value, setValue, defaultForHydration);
  const current = toNum(displayValue, min);
  const clamped = Math.min(Math.max(current, min), max);
  const pct = ((clamped - min) / (max - min)) * 100;

  // Read-only with nothing to show (no persisted value, and no default
  // unless this is a blank-form preview): say so, rather than parking the
  // thumb at the minimum as if the filler had chosen it.
  if (
    isReadOnly &&
    (displayValue === undefined || displayValue === null || displayValue === '')
  ) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          <span className="text-muted-foreground">—</span>
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  const readout = (
    <span className="flex items-baseline gap-[2px] tabular-nums">
      <span className="text-[15px] font-extrabold text-[var(--forms-accent,var(--forms-accent-default))]">
        {clamped}
      </span>
      {unit && (
        <span className="text-[12px] font-semibold text-[var(--forms-muted)]">
          {unit}
        </span>
      )}
    </span>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        {showValue && readout}
      </div>

      <div className="relative flex h-[20px] items-center">
        {/* rail */}
        <div className="absolute inset-x-0 h-[6px] rounded-[4px] bg-[var(--forms-page)]" />
        {/* filled portion */}
        <div
          className="absolute left-0 h-[6px] rounded-[4px] bg-[var(--forms-accent,var(--forms-accent-default))]"
          style={{ width: `${pct}%` }}
        />
        {/* thumb */}
        <div
          className="pointer-events-none absolute h-[20px] w-[20px] -translate-x-1/2 rounded-full border-2 border-[var(--forms-accent,var(--forms-accent-default))] bg-white shadow-[0_2px_6px_rgba(16,24,40,.22)]"
          style={{ left: `${pct}%` }}
        />
        <input
          id={element.id}
          type="range"
          min={min}
          max={max}
          step={step || 'any'}
          value={clamped}
          disabled={isReadOnly}
          onChange={(e) => setValue(e.target.value)}
          aria-label={label}
          className="absolute inset-x-0 h-[20px] w-full cursor-pointer opacity-0"
        />
      </div>

      {/* min / max bounds */}
      <div className="flex justify-between">
        <span className="text-[11.5px] text-[var(--forms-faint)]">
          {min}
          {unit}
        </span>
        <span className="text-[11.5px] text-[var(--forms-faint)]">
          {max}
          {unit}
        </span>
      </div>

      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default SliderInput;
